import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import homebtn from "../../images/Homebtn.svg";
import uploadFileToPreview from "../../images/Upload_file_to_Preview.svg";
import uploadProgressIcon from "../../images/imagesign.svg";
import smallImageIcon from "../../images/smallimage.svg";
import BackupImageSettingsHeader from "./BackupImageSettings/BackupImageSettingsHeader";
import BackupImageSettingsTabs from "./BackupImageSettings/BackupImageSettingsTabs";
import BackupImageUploaderPanel from "./BackupImageSettings/BackupImageUploaderPanel";
import BackupImagePreviewPanel from "./BackupImageSettings/BackupImagePreviewPanel";
import BackupImageSettingsFooter from "./BackupImageSettings/BackupImageSettingsFooter";
import BackupImageCancelConfirmationDialog from "./BackupImageSettings/BackupImageCancelConfirmationDialog";
import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_LABEL,
  UPLOAD_PROGRESS_STEPS,
  UPLOAD_PROGRESS_INTERVAL_MS,
  UPLOAD_PROGRESS_FINAL_STEP_DELAY_MS,
  UPLOAD_COMPLETE_HOLD_MS,
  INVALID_FILE_FORMAT_TEXT,
  PAGE_LOAD_ERROR_MESSAGE,
  isSupportedImageContent,
  isSupportedFileType,
  normalizeSizeLabel,
  formatSizeFromApi,
  parseSizeOptionsFromStorage,
  hasTrailingSizeLabel,
  formatFileSizeKb,
  truncateFileName,
  getOriginalFilename,
  deriveFileNameFromUrl,
  extractBackupImageData,
  emitter,
} from "../../Uitls/Util";
import {
  browseImages,
  getSizesData,
  saveBackupImageSettings,
  getBackupImageSettings,
  deleteBackupImageSettings,
  getSaveAssetsNodeApi,
  getMLRstatus,
} from "../../services/apis";
import ErrComponentToRender from "../../Uitls/apiErrorHandling";
import "../../styles/pages/BackupImageSettings.scss";

const BACKUP_IMAGE_SIZE_CACHE_KEY = "backupImageSizeCache";

const parseBannerDimensions = (sizeLabel) => {
  const parts = String(sizeLabel || "").match(/\d+/g);
  if (!parts || parts.length < 2) {
    return { width: 0, height: 0 };
  }

  const width = Number(parts[0]);
  const height = Number(parts[1]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return { width: 0, height: 0 };
  }

  return { width, height };
};

const readImageDimensions = (file) =>
  new Promise((resolve) => {
    if (!file) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    image.onload = () => {
      const width = Number(image.naturalWidth) || 0;
      const height = Number(image.naturalHeight) || 0;
      cleanup();
      resolve({ width, height });
    };

    image.onerror = () => {
      cleanup();
      resolve({ width: 0, height: 0 });
    };

    image.src = objectUrl;
  });

const parseBackupImageSizeCache = () => {
  if (typeof window === "undefined") return {};
  try {
    const rawCache = window.localStorage.getItem(BACKUP_IMAGE_SIZE_CACHE_KEY);
    if (!rawCache) return {};
    const parsedCache = JSON.parse(rawCache);
    return parsedCache && typeof parsedCache === "object" ? parsedCache : {};
  } catch (error) {
    return {};
  }
};

const getCachedBackupImageSize = (assetId) => {
  const normalizedAssetId = String(assetId || "").trim();
  if (!normalizedAssetId) return 0;
  const cache = parseBackupImageSizeCache();
  const entry = cache?.[normalizedAssetId] || {};
  const cachedFileSize = Number(entry?.fileSize);
  return Number.isFinite(cachedFileSize) && cachedFileSize > 0
    ? cachedFileSize
    : 0;
};

const setCachedBackupImageSize = (assetId, fileSize) => {
  if (typeof window === "undefined") return;
  const normalizedAssetId = String(assetId || "").trim();
  const normalizedFileSize = Number(fileSize);
  if (
    !normalizedAssetId ||
    !Number.isFinite(normalizedFileSize) ||
    normalizedFileSize <= 0
  ) {
    return;
  }

  const cache = parseBackupImageSizeCache();
  cache[normalizedAssetId] = {
    fileSize: normalizedFileSize,
  };

  try {
    window.localStorage.setItem(
      BACKUP_IMAGE_SIZE_CACHE_KEY,
      JSON.stringify(cache),
    );
  } catch (error) {
    // Ignore storage write failures and continue.
  }
};

export default function BackupImageSettings() {
  const history = useHistory();
  const location = useLocation();
  const { bannerId } = useParams();
  const normalizedBannerId = String(bannerId || "").trim();
  const fileInputRef = useRef(null);
  const previewObjectUrlRef = useRef("");
  const uploadProgressTimerRef = useRef(null);
  const uploadCompletionTimerRef = useRef(null);
  const uploadProgressValueRef = useRef(0);
  const uploadOperationIdRef = useRef(0);
  const uploadRequestCancelSourceRef = useRef(null);
  const uploadVisualPausedRef = useRef(false);
  const uploadCompletionPayloadRef = useRef(null);
  const tabLoadRequestIdRef = useRef(0);
  const syncedBackupImageDimensionIdsRef = useRef(new Set());
  const hasLoadedOnceRef = useRef(false);
  const baseBannerIdRef = useRef(location?.state?.baseBannerId || bannerId);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [assetDetails, setAssetDetails] = useState(null);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewAssetId, setPreviewAssetId] = useState(normalizedBannerId);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dimensionImageMap, setDimensionImageMap] = useState({});
  const [cancelConfirmationType, setCancelConfirmationType] = useState("");
  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    fileName: "",
    fileSize: "",
  });

  useEffect(() => {
    uploadProgressValueRef.current = uploadStatus.progress;
  }, [uploadStatus.progress]);

  const clearObjectPreviewUrl = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = "";
    }
  }, []);

  const clearUploadRequest = useCallback(() => {
    if (uploadRequestCancelSourceRef.current) {
      uploadRequestCancelSourceRef.current.cancel("UPLOAD_REQUEST_CANCELLED");
      uploadRequestCancelSourceRef.current = null;
    }
  }, []);

  const clearUploadProgressTimer = useCallback(() => {
    if (uploadProgressTimerRef.current) {
      clearInterval(uploadProgressTimerRef.current);
      uploadProgressTimerRef.current = null;
    }
  }, []);

  const clearUploadCompletionTimer = useCallback(() => {
    if (uploadCompletionTimerRef.current) {
      clearTimeout(uploadCompletionTimerRef.current);
      uploadCompletionTimerRef.current = null;
    }
  }, []);

  const startSteppedProgressTimer = useCallback(() => {
    clearUploadProgressTimer();
    uploadProgressTimerRef.current = setInterval(() => {
      setUploadStatus((previous) => {
        if (!previous.isUploading || uploadVisualPausedRef.current)
          return previous;
        const nextProgressStep = UPLOAD_PROGRESS_STEPS.find(
          (stepValue) => stepValue > previous.progress,
        );
        if (!nextProgressStep) return previous;
        return { ...previous, progress: nextProgressStep };
      });
    }, UPLOAD_PROGRESS_INTERVAL_MS);
  }, [clearUploadProgressTimer]);

  const resetUploadStatus = useCallback(() => {
    clearUploadProgressTimer();
    clearUploadCompletionTimer();
    uploadVisualPausedRef.current = false;
    uploadCompletionPayloadRef.current = null;
    uploadProgressValueRef.current = 0;
    setUploadStatus({
      isUploading: false,
      progress: 0,
      fileName: "",
      fileSize: "",
    });
  }, [clearUploadCompletionTimer, clearUploadProgressTimer]);

  const startUploadProgress = useCallback(
    (file) => {
      uploadVisualPausedRef.current = false;
      uploadCompletionPayloadRef.current = null;
      clearUploadCompletionTimer();
      clearUploadProgressTimer();
      uploadProgressValueRef.current = UPLOAD_PROGRESS_STEPS[0];
      setUploadStatus({
        isUploading: true,
        progress: UPLOAD_PROGRESS_STEPS[0],
        fileName: truncateFileName(file?.name),
        fileSize: formatFileSizeKb(file?.size),
      });
      startSteppedProgressTimer();
    },
    [
      clearUploadCompletionTimer,
      clearUploadProgressTimer,
      startSteppedProgressTimer,
    ],
  );

  const updateDimensionImageEntry = useCallback((assetId, updater) => {
    if (!assetId) return;
    setDimensionImageMap((previous) => {
      const currentEntry = previous[assetId] || {};
      const nextEntry =
        typeof updater === "function" ? updater(currentEntry) : updater;
      return {
        ...previous,
        [assetId]: {
          ...currentEntry,
          ...nextEntry,
        },
      };
    });
  }, []);

  const fetchImageFileSize = useCallback(async (imageUrl) => {
    const url = String(imageUrl || "").trim();
    if (!url) return 0;

    try {
      const headResponse = await fetch(url, { method: "HEAD" });
      const contentLength = Number(
        headResponse?.headers?.get?.("content-length"),
      );
      if (Number.isFinite(contentLength) && contentLength > 0) {
        return contentLength;
      }
    } catch (error) {
      // Ignore and fall through to GET fallback.
    }

    try {
      const getResponse = await fetch(url);
      if (!getResponse?.ok) return 0;
      const blob = await getResponse.blob();
      const blobSize = Number(blob?.size);
      if (Number.isFinite(blobSize) && blobSize > 0) {
        return blobSize;
      }
    } catch (error) {
      // Ignore and return unknown size.
    }

    return 0;
  }, []);

  const navigateToEditor = useCallback(() => {
    const baseBannerId = baseBannerIdRef.current || bannerId;
    history.push(`/banner-editor/${baseBannerId}`);
  }, [bannerId, history]);

  const navigateToHome = useCallback(() => {
    history.push("/");
  }, [history]);

  useEffect(() => {
    const incomingBaseBannerId = location?.state?.baseBannerId;
    if (incomingBaseBannerId) {
      baseBannerIdRef.current = incomingBaseBannerId;
    }
  }, [location?.state?.baseBannerId]);

  useEffect(() => {
    if (uploadStatus.isUploading) return;
    const currentEntry = dimensionImageMap[bannerId] || {};
    const draftImageUrl = currentEntry?.draftImageUrl || "";
    const savedImageUrl = currentEntry?.savedImageUrl || "";
    const hasLocalBlobPreviewForCurrentBanner =
      previewAssetId === normalizedBannerId &&
      String(previewUrl || "").startsWith("blob:");
    if (draftImageUrl) {
      setPreviewAssetId(normalizedBannerId);
      if (!hasLocalBlobPreviewForCurrentBanner) {
        setPreviewUrl(draftImageUrl);
      }
      setSelectedFile({
        name: currentEntry?.draftFileName || "backup-image",
        size: Number(currentEntry?.draftFileSize) || 0,
      });
      return;
    }

    if (savedImageUrl) {
      const savedFileSize = Number(currentEntry?.savedFileSize);
      setPreviewAssetId(normalizedBannerId);
      if (!hasLocalBlobPreviewForCurrentBanner) {
        setPreviewUrl(savedImageUrl);
      }
      setSelectedFile({
        name:
          currentEntry?.savedFileName ||
          deriveFileNameFromUrl(savedImageUrl) ||
          "backup-image",
        size:
          Number.isFinite(savedFileSize) && savedFileSize > 0
            ? savedFileSize
            : 0,
      });
      return;
    }

    setPreviewAssetId(normalizedBannerId);
    setSelectedFile(null);
    setPreviewUrl("");
  }, [
    bannerId,
    dimensionImageMap,
    normalizedBannerId,
    previewAssetId,
    previewUrl,
    uploadStatus.isUploading,
  ]);

  const runUploadCompletionSequence = useCallback(
    (payload) => {
      const { operationId, imageUrl, cleanedFileName, fileSize } =
        payload || {};
      if (!operationId || !imageUrl) return;
      if (operationId !== uploadOperationIdRef.current) return;

      uploadCompletionPayloadRef.current = payload;
      if (uploadVisualPausedRef.current) return;

      clearUploadProgressTimer();
      clearUploadCompletionTimer();

      const completionSteps = [...UPLOAD_PROGRESS_STEPS, 100].filter(
        (stepValue) => stepValue > uploadProgressValueRef.current,
      );

      const finalizeUpload = () => {
        if (operationId !== uploadOperationIdRef.current) return;
        updateDimensionImageEntry(bannerId, {
          draftImageUrl: imageUrl,
          draftFileName: cleanedFileName,
          draftFileSize: fileSize || 0,
        });
        setUploadStatus((previous) => ({
          ...previous,
          isUploading: false,
          progress: 100,
        }));
        uploadCompletionTimerRef.current = null;
        uploadCompletionPayloadRef.current = null;
      };

      const runCompletionStep = (stepIndex = 0) => {
        if (operationId !== uploadOperationIdRef.current) return;
        if (uploadVisualPausedRef.current) return;

        const nextStep = completionSteps[stepIndex];
        if (!nextStep) {
          finalizeUpload();
          return;
        }

        uploadCompletionTimerRef.current = setTimeout(() => {
          if (operationId !== uploadOperationIdRef.current) return;
          if (uploadVisualPausedRef.current) {
            uploadCompletionTimerRef.current = null;
            return;
          }

          setUploadStatus((previous) => ({
            ...previous,
            progress: nextStep,
          }));

          if (nextStep === 100) {
            uploadCompletionTimerRef.current = setTimeout(() => {
              if (operationId !== uploadOperationIdRef.current) return;
              if (uploadVisualPausedRef.current) {
                uploadCompletionTimerRef.current = null;
                return;
              }
              finalizeUpload();
            }, UPLOAD_COMPLETE_HOLD_MS);
            return;
          }

          runCompletionStep(stepIndex + 1);
        }, UPLOAD_PROGRESS_FINAL_STEP_DELAY_MS);
      };

      runCompletionStep(0);
    },
    [
      bannerId,
      clearUploadCompletionTimer,
      clearUploadProgressTimer,
      updateDimensionImageEntry,
    ],
  );

  const pauseUploadVisualProgress = useCallback(() => {
    if (!uploadStatus.isUploading) return;
    uploadVisualPausedRef.current = true;
    clearUploadProgressTimer();
    clearUploadCompletionTimer();
  }, [
    clearUploadCompletionTimer,
    clearUploadProgressTimer,
    uploadStatus.isUploading,
  ]);

  const resumeUploadVisualProgress = useCallback(() => {
    if (!uploadVisualPausedRef.current) return;
    uploadVisualPausedRef.current = false;

    const pendingCompletionPayload = uploadCompletionPayloadRef.current;
    if (pendingCompletionPayload) {
      runUploadCompletionSequence(pendingCompletionPayload);
      return;
    }

    if (uploadStatus.isUploading) {
      startSteppedProgressTimer();
    }
  }, [
    runUploadCompletionSequence,
    startSteppedProgressTimer,
    uploadStatus.isUploading,
  ]);

  const handleOpenUploadCancelConfirmation = useCallback(() => {
    pauseUploadVisualProgress();
    setCancelConfirmationType("upload");
  }, [pauseUploadVisualProgress]);

  const handleOpenDeleteConfirmation = useCallback(() => {
    const currentEntry = dimensionImageMap[bannerId] || {};
    const hasLocalSelection =
      previewAssetId === normalizedBannerId && Boolean(selectedFile);
    const hasMappedSelection = Boolean(
      currentEntry?.draftImageUrl || currentEntry?.savedImageUrl,
    );
    if (!hasLocalSelection && !hasMappedSelection) return;
    setCancelConfirmationType("delete");
  }, [
    bannerId,
    dimensionImageMap,
    normalizedBannerId,
    previewAssetId,
    selectedFile,
  ]);

  const handleFooterCancelClick = useCallback(() => {
    setCancelConfirmationType("editor");
  }, []);

  const handleCloseCancelConfirmation = useCallback(() => {
    const shouldResumeUploadVisualProgress =
      cancelConfirmationType === "upload";
    setCancelConfirmationType("");
    if (shouldResumeUploadVisualProgress) {
      resumeUploadVisualProgress();
    }
  }, [cancelConfirmationType, resumeUploadVisualProgress]);

  const handleCancelConfirmationDialogClose = useCallback(
    (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        return;
      }
      handleCloseCancelConfirmation();
    },
    [handleCloseCancelConfirmation],
  );

  const handleConfirmBackToEditor = useCallback(() => {
    setCancelConfirmationType("");
    navigateToEditor();
  }, [navigateToEditor]);

  const selectedSizeLabel = useMemo(() => {
    const selectedOption = sizeOptions.find(
      (option) => option.value === bannerId,
    );
    if (selectedOption?.label) return selectedOption.label;
    const width = Number(assetDetails?.data.width);
    const height = Number(assetDetails?.data.height);
    if (Number.isFinite(width) && Number.isFinite(height)) {
      return `${width}X${height}`;
    }
    return "";
  }, [
    assetDetails?.data.height,
    assetDetails?.data.width,
    bannerId,
    sizeOptions,
  ]);

  const currentBannerDimensions = useMemo(() => {
    const parsedDimensions = parseBannerDimensions(selectedSizeLabel);
    if (parsedDimensions.width > 0 && parsedDimensions.height > 0) {
      return parsedDimensions;
    }

    const width = Number(assetDetails?.data.width);
    const height = Number(assetDetails?.data.height);
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return { width: 0, height: 0 };
    }

    return { width, height };
  }, [assetDetails?.data.height, assetDetails?.data.width, selectedSizeLabel]);

  const backupImageDimensionsLabel = useMemo(() => {
    const { width, height } = currentBannerDimensions;
    if (!Number.isFinite(width) || !Number.isFinite(height)) return "\u2014 px";
    return `${width} x ${height} px`;
  }, [currentBannerDimensions]);

  const bannerSubtitle = useMemo(() => {
    const assetName = String(assetDetails?.data.assetName || "").trim();
    const normalizedSizeLabel = normalizeSizeLabel(selectedSizeLabel);
    if (!assetName) return normalizedSizeLabel;
    if (
      !normalizedSizeLabel ||
      hasTrailingSizeLabel(assetName, normalizedSizeLabel)
    ) {
      return assetName;
    }
    return `${assetName}_${normalizedSizeLabel}`;
  }, [assetDetails?.data.assetName, selectedSizeLabel]);

  const setPreviewFromFile = useCallback(
    (file) => {
      clearObjectPreviewUrl();
      const objectUrl = URL.createObjectURL(file);
      previewObjectUrlRef.current = objectUrl;
      setPreviewAssetId(normalizedBannerId);
      setPreviewUrl(objectUrl);
    },
    [clearObjectPreviewUrl, normalizedBannerId],
  );

  const showInvalidFileFormatToast = useCallback(() => {
    toast.error(
      <div className="notificationText">{INVALID_FILE_FORMAT_TEXT}</div>,
    );
  }, []);

  const showDimensionMismatchToast = useCallback(
    (uploadedDimensions, selectedDimensions) => {
      toast.error(
        <div className="notificationText">
          {`Uploaded image dimensions (${uploadedDimensions.width} x ${uploadedDimensions.height} px) do not match the selected banner size (${selectedDimensions.width} x ${selectedDimensions.height} px).`}
        </div>,
      );
    },
    [],
  );

  const validateFile = useCallback(
    async (file) => {
      if (!file) {
        return false;
      }

      if (!isSupportedFileType(file)) {
        showInvalidFileFormatToast();
        return false;
      }

      if (!(await isSupportedImageContent(file))) {
        showInvalidFileFormatToast();
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          <div className="notificationText">
            {`Image size should be within ${MAX_FILE_SIZE_LABEL}.`}
          </div>,
        );
        return false;
      }

      const selectedWidth = Number(currentBannerDimensions?.width) || 0;
      const selectedHeight = Number(currentBannerDimensions?.height) || 0;
      if (selectedWidth > 0 && selectedHeight > 0) {
        const uploadedDimensions = await readImageDimensions(file);
        if (
          uploadedDimensions.width > 0 &&
          uploadedDimensions.height > 0 &&
          (uploadedDimensions.width !== selectedWidth ||
            uploadedDimensions.height !== selectedHeight)
        ) {
          showDimensionMismatchToast(uploadedDimensions, {
            width: selectedWidth,
            height: selectedHeight,
          });
          return false;
        }
      }

      return true;
    },
    [
      currentBannerDimensions?.height,
      currentBannerDimensions?.width,
      showDimensionMismatchToast,
      showInvalidFileFormatToast,
    ],
  );

  const syncSavedImagesForOptions = useCallback(
    (options, requestId) => {
      const validOptions = (Array.isArray(options) ? options : []).filter(
        (option) => option?.value,
      );
      const pendingDimensionIds = validOptions
        .map((option) => String(option?.value || "").trim())
        .filter(Boolean)
        .filter(
          (dimensionId) =>
            !syncedBackupImageDimensionIdsRef.current.has(dimensionId),
        );

      pendingDimensionIds.forEach((dimensionId) => {
        getBackupImageSettings(
          dimensionId,
          (backupResponse, backupErr, backupErrorCode) => {
            if (requestId !== tabLoadRequestIdRef.current) return;

            if (backupErr) {
              if (backupErrorCode && backupErrorCode !== 404) {
                return;
              }
              syncedBackupImageDimensionIdsRef.current.add(dimensionId);
              updateDimensionImageEntry(dimensionId, {
                savedImageUrl: "",
                savedFileName: "",
                savedFileSize: 0,
              });
              return;
            }

            const { imageUrl, fileName, fileSize } =
              extractBackupImageData(backupResponse);
            const cachedFileSize = getCachedBackupImageSize(dimensionId);
            const resolvedSavedFileSize =
              fileSize > 0 ? fileSize : cachedFileSize;
            updateDimensionImageEntry(dimensionId, {
              savedImageUrl: imageUrl,
              savedFileName: fileName,
              savedFileSize: resolvedSavedFileSize,
            });
            syncedBackupImageDimensionIdsRef.current.add(dimensionId);

            if (resolvedSavedFileSize > 0) {
              setCachedBackupImageSize(dimensionId, resolvedSavedFileSize);
            }

            if (!imageUrl || resolvedSavedFileSize > 0) return;
            fetchImageFileSize(imageUrl).then((resolvedFileSize) => {
              if (requestId !== tabLoadRequestIdRef.current) return;
              if (!Number.isFinite(resolvedFileSize) || resolvedFileSize <= 0) {
                return;
              }
              updateDimensionImageEntry(dimensionId, {
                savedFileSize: resolvedFileSize,
              });
              setCachedBackupImageSize(dimensionId, resolvedFileSize);
            });
          },
        );
      });
    },
    [fetchImageFileSize, updateDimensionImageEntry],
  );

  const loadSizeOptions = useCallback(
    (damDocNumber, currentAsset, requestId) => {
      const isCurrentRequest = () => requestId === tabLoadRequestIdRef.current;
      const storedOptions = parseSizeOptionsFromStorage();
      const fallbackOption = {
        label:
          normalizeSizeLabel(
            `${currentAsset?.width || ""}X${currentAsset?.height || ""}`,
          ) || "CURRENT SIZE",
        value: currentAsset?.id || bannerId,
      };

      if (storedOptions.length > 0) {
        if (
          !storedOptions.some((option) => option.value === fallbackOption.value)
        ) {
          storedOptions.push(fallbackOption);
        }
        if (!isCurrentRequest()) return;
        setSizeOptions(storedOptions);
        syncSavedImagesForOptions(storedOptions, requestId);
        return;
      }

      if (!damDocNumber) {
        if (!isCurrentRequest()) return;
        const singleOption = [fallbackOption];
        setSizeOptions(singleOption);
        syncSavedImagesForOptions(singleOption, requestId);
        return;
      }

      getSizesData(damDocNumber, (response, err) => {
        if (!isCurrentRequest()) return;
        if (err || response?.status !== 200 || !response?.data) {
          const fallbackOptions = [fallbackOption];
          setSizeOptions(fallbackOptions);
          syncSavedImagesForOptions(fallbackOptions, requestId);
          return;
        }

        const options = Object.keys(response.data).map((assetId) => ({
          label: formatSizeFromApi(response.data[assetId]),
          value: assetId,
        }));

        if (!options.some((option) => option.value === fallbackOption.value)) {
          options.push(fallbackOption);
        }

        setSizeOptions(options);
        syncSavedImagesForOptions(options, requestId);
        localStorage.setItem("allBannersForVeevaId", JSON.stringify(options));
      });
    },
    [bannerId, syncSavedImagesForOptions],
  );

  const mlrStatusCheck = useCallback((damDocNumber) => {
    getMLRstatus(damDocNumber, (response, err, errorCode) => {
      if (err) {
        return;
      }
      if (
        response?.data?.mlrStatus !== null &&
        response?.data?.mlrStatus?.length === 0 &&
        response?.data?.mlrStatus !== undefined &&
        response?.data?.mlrStatus !== "Draft" &&
        response?.data?.mlrStatus !== "Reviewed with Minor Changes" &&
        response?.data?.mlrStatus !== "Approved with Minor Changes" &&
        response?.data?.mlrStatus !== "Not Submitted"
      ) {
        toast.error(
          <div className="notificationText">
            You are not allowed to access this page.
          </div>,
        );
        setTimeout(() => {
          navigateToHome();
        }, 200);
      }
    });
  }, []);

  useEffect(() => {
    const handleAutoSync = (data) => {
      const assetDocNumbers = [
        assetDetails?.data?.veevaDocNumber,
        assetDetails?.data?.damDocNumber,
        assetDetails?.data?.documentNumber,
      ].filter(Boolean);

      const eventDocNumbers = [
        data?.veevaDocNumber,
        data?.damDocNumber,
        data?.documentNumber,
        data?.elementName,
      ].filter(Boolean);

      const isMatch =
        assetDocNumbers.length > 0 &&
        eventDocNumbers.length > 0 &&
        assetDocNumbers.some((assetDoc) =>
          eventDocNumbers.some(
            (eventDoc) =>
              String(assetDoc).trim().toLowerCase() ===
              String(eventDoc).trim().toLowerCase(),
          ),
        );

      if (isMatch) {
        mlrStatusCheck(assetDetails?.data?.veevaDocNumber);
      }
    };

    emitter.on("autoSync", handleAutoSync);
    return () => {
      emitter.off("autoSync", handleAutoSync);
    };
  }, [assetDetails, mlrStatusCheck]);

  const loadPageData = useCallback(() => {
    const requestId = tabLoadRequestIdRef.current + 1;
    tabLoadRequestIdRef.current = requestId;
    uploadOperationIdRef.current += 1;
    toast.dismiss();
    clearUploadRequest();
    clearObjectPreviewUrl();
    resetUploadStatus();
    setLoading(!hasLoadedOnceRef.current);
    setLoadError("");
    setSaving(false);
    setIsDragActive(false);
    setCancelConfirmationType("");
    getSaveAssetsNodeApi(bannerId, (response, err, errorCode) => {
      if (requestId !== tabLoadRequestIdRef.current) return;
      if (err) {
        const errComponent = ErrComponentToRender(errorCode);
        toast.error(errComponent);
        setAssetDetails(null);
        setSizeOptions([]);
        setLoadError(PAGE_LOAD_ERROR_MESSAGE);
        setLoading(false);
        return;
      }
      const data = response || {};
      mlrStatusCheck(data?.data?.veevaDocNumber);
      setAssetDetails(data);
      loadSizeOptions(data?.damDocNumber, data, requestId);
      hasLoadedOnceRef.current = true;
      setLoading(false);
    });
  }, [
    bannerId,
    clearObjectPreviewUrl,
    clearUploadRequest,
    loadSizeOptions,
    resetUploadStatus,
  ]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    return () => {
      uploadOperationIdRef.current += 1;
      clearUploadRequest();
      clearObjectPreviewUrl();
      clearUploadProgressTimer();
      clearUploadCompletionTimer();
    };
  }, [
    clearUploadRequest,
    clearObjectPreviewUrl,
    clearUploadCompletionTimer,
    clearUploadProgressTimer,
  ]);

  useEffect(() => {
    const preventDropOutsideUploader = (event) => {
      const targetElement =
        event.target instanceof Element ? event.target : null;
      if (targetElement?.closest(".upload-dropzone")) {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener("dragover", preventDropOutsideUploader);
    window.addEventListener("drop", preventDropOutsideUploader);

    return () => {
      window.removeEventListener("dragover", preventDropOutsideUploader);
      window.removeEventListener("drop", preventDropOutsideUploader);
    };
  }, []);

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileSelection = async (fileList) => {
    const file = fileList?.[0];
    if (!(await validateFile(file))) return;
    setPreviewAssetId(normalizedBannerId);
    setSelectedFile(file);
    setPreviewFromFile(file);
    uploadSelectedFile(file);
  };

  const handleFileInputChange = (event) => {
    handleFileSelection(event.target.files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    handleFileSelection(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleSizeTabClick = (assetId) => {
    if (!assetId || assetId === bannerId) return;
    toast.dismiss();
    history.push({
      pathname: `/backup-image-settings/${assetId}`,
      state: {
        ...(location?.state || {}),
        baseBannerId: baseBannerIdRef.current || bannerId,
      },
    });
  };

  const handleCancelUpload = useCallback(() => {
    uploadOperationIdRef.current += 1;
    clearUploadRequest();
    setSaving(false);
    clearObjectPreviewUrl();
    setPreviewAssetId(normalizedBannerId);
    setSelectedFile(null);
    setPreviewUrl("");
    updateDimensionImageEntry(bannerId, {
      draftImageUrl: "",
      draftFileName: "",
      draftFileSize: 0,
    });
    resetUploadStatus();
  }, [
    bannerId,
    clearObjectPreviewUrl,
    clearUploadRequest,
    normalizedBannerId,
    resetUploadStatus,
    updateDimensionImageEntry,
  ]);

  const handleConfirmUploadCancel = useCallback(() => {
    setCancelConfirmationType("");
    handleCancelUpload();
  }, [handleCancelUpload]);

  const handleRemoveSelectedFile = useCallback(() => {
    uploadOperationIdRef.current += 1;
    clearUploadRequest();
    clearObjectPreviewUrl();
    setPreviewAssetId(normalizedBannerId);
    setSelectedFile(null);
    setPreviewUrl("");
    updateDimensionImageEntry(bannerId, {
      draftImageUrl: "",
      draftFileName: "",
      draftFileSize: 0,
      savedImageUrl: "",
      savedFileName: "",
      savedFileSize: 0,
    });
    resetUploadStatus();
  }, [
    bannerId,
    clearObjectPreviewUrl,
    clearUploadRequest,
    normalizedBannerId,
    resetUploadStatus,
    updateDimensionImageEntry,
  ]);

  const handleConfirmDeleteImage = useCallback(() => {
    setCancelConfirmationType("");

    const currentEntry = dimensionImageMap[bannerId] || {};
    const hasSavedImage = Boolean(currentEntry?.savedImageUrl);

    if (!hasSavedImage) {
      handleRemoveSelectedFile();
      return;
    }

    setSaving(true);
    deleteBackupImageSettings(bannerId, (response, err, errorCode) => {
      setSaving(false);
      if (err) {
        const errComponent = ErrComponentToRender(errorCode);
        toast.error(errComponent);
        return;
      }

      const isSuccess =
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data?.success;

      if (isSuccess) {
        toast.success(
          <div className="notificationText">
            Backup image settings deleted successfully.
          </div>,
        );

        handleRemoveSelectedFile();
      }
    });
  }, [bannerId, dimensionImageMap, handleRemoveSelectedFile]);

  const uploadSelectedFile = useCallback(
    (file, dimensionMismatchMessage = "") => {
      if (!file) return;
      const operationId = uploadOperationIdRef.current + 1;
      uploadOperationIdRef.current = operationId;
      clearUploadRequest();
      const cancelSource = axios.CancelToken.source();
      uploadRequestCancelSourceRef.current = cancelSource;
      startUploadProgress(file);
      const cleanedFileName = file.name
        .replace(/\((?!\d+\))/g, "")
        .replace(/(?<!\(\d+)\)/g, "")
        .replace(/\s+/g, "");
      const fileToUpload = new File([file], cleanedFileName, {
        type: file.type,
      });
      const formData = new FormData();
      formData.append("fileName", fileToUpload);

      browseImages(
        formData,
        (uploadResponse, uploadErr, uploadErrorCode) => {
          if (operationId !== uploadOperationIdRef.current) {
            return;
          }

          uploadRequestCancelSourceRef.current = null;

          if (uploadErr) {
            if (axios.isCancel(uploadErr)) {
              resetUploadStatus();
              return;
            }
            const errComponent = ErrComponentToRender(uploadErrorCode);
            toast.error(errComponent);
            handleCancelUpload();
            return;
          }

          const imageUrl = uploadResponse?.data?.discription;
          if (!imageUrl) {
            toast.error(
              <div className="notificationText">
                Failed to upload backup image.
              </div>,
            );
            handleCancelUpload();
            return;
          }

          runUploadCompletionSequence({
            operationId,
            imageUrl,
            cleanedFileName,
            fileSize: file?.size || 0,
          });
        },
        { cancelToken: cancelSource.token },
      );
    },
    [
      clearUploadRequest,
      handleCancelUpload,
      resetUploadStatus,
      runUploadCompletionSequence,
      startUploadProgress,
    ],
  );

  const handleSaveSettings = () => {
    if (saving || uploadStatus.isUploading) return;

    const dimensionIds = sizeOptions
      .map((option) => option?.value)
      .filter(Boolean);

    const draftsToSave = dimensionIds
      .map((dimensionId) => {
        const entry = dimensionImageMap[dimensionId] || {};
        const imageUrl = entry?.draftImageUrl || "";
        const fileName = entry?.draftFileName || "";
        if (!imageUrl) return null;
        return { assetId: dimensionId, imageUrl, fileName };
      })
      .filter(Boolean);

    if (draftsToSave.length === 0) return;

    const operationId = uploadOperationIdRef.current;
    setSaving(true);

    const saveDraftImage = ({ assetId, imageUrl, fileName }) =>
      new Promise((resolve, reject) => {
        saveBackupImageSettings(
          { assetId, imageUrl },
          (response, err, errorCode) => {
            if (err) {
              reject({ err, errorCode });
              return;
            }
            resolve({ assetId, imageUrl, fileName });
          },
        );
      });

    Promise.all(draftsToSave.map(saveDraftImage))
      .then((savedEntries) => {
        if (operationId !== uploadOperationIdRef.current) return;

        setDimensionImageMap((previous) => {
          const nextMap = { ...previous };
          savedEntries.forEach(({ assetId, imageUrl, fileName }) => {
            const existingEntry = nextMap[assetId] || {};
            nextMap[assetId] = {
              ...existingEntry,
              savedImageUrl: imageUrl,
              savedFileName: fileName,
              savedFileSize:
                Number(existingEntry?.draftFileSize) ||
                Number(existingEntry?.savedFileSize) ||
                0,
              draftImageUrl: "",
              draftFileName: "",
              draftFileSize: 0,
            };
          });
          return nextMap;
        });

        savedEntries.forEach(({ assetId }) => {
          const existingEntry = dimensionImageMap[assetId] || {};
          const savedFileSize =
            Number(existingEntry?.draftFileSize) ||
            Number(existingEntry?.savedFileSize) ||
            0;
          if (savedFileSize > 0) {
            setCachedBackupImageSize(assetId, savedFileSize);
          }
        });

        toast.success(
          <div className="notificationText">
            Backup image settings saved successfully.
          </div>,
          {
            className: "global-toast backup-image-success-toast",
            bodyClassName: "global-toast-body backup-image-success-toast-body",
            style: {
              minHeight: "56px",
              height: "56px",
              padding: "10px 18px",
            },
            bodyStyle: {
              flex: "0 0 auto",
              minHeight: 0,
              margin: 0,
              padding: 0,
            },
          },
        );
        setSaving(false);
        navigateToEditor();
      })
      .catch(({ errorCode }) => { 
        if (operationId !== uploadOperationIdRef.current) return;
        const errComponent = ErrComponentToRender(errorCode);
        toast.error(errComponent);
        setSaving(false);
      });
  };

  const handleRetryLoad = useCallback(() => {
    loadPageData();
  }, [loadPageData]);

  const dimensionIds = useMemo(
    () => sizeOptions.map((option) => option?.value).filter(Boolean),
    [sizeOptions],
  );

  const hasMultipleDimensions = dimensionIds.length > 1;

  const hasAnyDraftUploads = useMemo(
    () =>
      dimensionIds.some((dimensionId) =>
        Boolean(dimensionImageMap[dimensionId]?.draftImageUrl),
      ),
    [dimensionIds, dimensionImageMap],
  );

  const allDimensionsHaveImage = useMemo(
    () =>
      dimensionIds.length > 0 &&
      dimensionIds.every((dimensionId) => {
        const entry = dimensionImageMap[dimensionId] || {};
        return Boolean(entry?.draftImageUrl || entry?.savedImageUrl);
      }),
    [dimensionIds, dimensionImageMap],
  );

  const currentDimensionHasDraft = Boolean(
    dimensionImageMap[bannerId]?.draftImageUrl,
  );

  const currentDimensionEntry = useMemo(
    () => dimensionImageMap[bannerId] || {},
    [bannerId, dimensionImageMap],
  );
  const currentDraftImageUrl = currentDimensionEntry?.draftImageUrl || "";
  const currentSavedImageUrl = currentDimensionEntry?.savedImageUrl || "";

  const displayedPreviewUrl = useMemo(
    () =>
      previewAssetId === normalizedBannerId && previewUrl
        ? previewUrl
        : currentDraftImageUrl || currentSavedImageUrl || "",
    [
      currentDraftImageUrl,
      currentSavedImageUrl,
      normalizedBannerId,
      previewAssetId,
      previewUrl,
    ],
  );

  const displayedSelectedFile = useMemo(
    () =>
      previewAssetId === normalizedBannerId && selectedFile
        ? selectedFile
        : currentDraftImageUrl
          ? {
              name: currentDimensionEntry?.draftFileName || "backup-image",
              size: Number(currentDimensionEntry?.draftFileSize) || 0,
            }
          : currentSavedImageUrl
            ? {
                name:
                  currentDimensionEntry?.savedFileName ||
                  deriveFileNameFromUrl(currentSavedImageUrl) ||
                  "backup-image",
                size: Number(currentDimensionEntry?.savedFileSize) || 0,
              }
            : null,
    [
      currentDimensionEntry,
      currentDraftImageUrl,
      currentSavedImageUrl,
      normalizedBannerId,
      previewAssetId,
      selectedFile,
    ],
  );

  const isSaveDisabled =
    saving ||
    uploadStatus.isUploading ||
    (hasMultipleDimensions
      ? !allDimensionsHaveImage || !hasAnyDraftUploads
      : !currentDimensionHasDraft);

  const selectedFileSizeLabel = useMemo(() => {
    if (!displayedSelectedFile) return "";
    const draftFileSize = Number(currentDimensionEntry?.draftFileSize);
    const savedFileSize = Number(currentDimensionEntry?.savedFileSize);
    const selectedSize = Number(displayedSelectedFile?.size);

    const resolvedSize =
      Number.isFinite(draftFileSize) && draftFileSize > 0
        ? draftFileSize
        : Number.isFinite(savedFileSize) && savedFileSize > 0
          ? savedFileSize
          : Number.isFinite(selectedSize) && selectedSize > 0
            ? selectedSize
            : 0;

    if (!resolvedSize) return "";
    return formatFileSizeKb(resolvedSize);
  }, [currentDimensionEntry, displayedSelectedFile]);

  if (loadError && !loading) {
    return (
      <div className="backup-image-settings-loading">
        <div className="backup-image-settings-error-card">
          <div className="backup-image-settings-error-title">
            Unable to Load Page
          </div>
          <div className="backup-image-settings-error-message">{loadError}</div>
          <button
            type="button"
            className="backup-image-settings-error-retry"
            onClick={handleRetryLoad}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && !assetDetails) {
    return (
      <div className="backup-image-settings-loading">
        <CircularProgress size={48} />
      </div>
    );
  }

  return (
    <div className="backup-image-settings-page">
      <BackupImageSettingsHeader
        homeButtonSrc={homebtn}
        bannerSubtitle={bannerSubtitle}
        onNavigateHome={navigateToHome}
      />

      <BackupImageSettingsTabs
        sizeOptions={sizeOptions}
        selectedBannerId={bannerId}
        onTabClick={handleSizeTabClick}
      />

      <div className="backup-image-settings-content">
        {loading ? (
          <div className="backup-image-settings-content-loading">
            <CircularProgress size={32} />
          </div>
        ) : null}
        <BackupImageUploaderPanel
          isDragActive={isDragActive}
          uploadStatus={uploadStatus}
          selectedFile={displayedSelectedFile}
          selectedFileSizeLabel={selectedFileSizeLabel}
          backupImageDimensionsLabel={backupImageDimensionsLabel}
          fileInputRef={fileInputRef}
          maxFileSizeLabel={MAX_FILE_SIZE_LABEL}
          uploadProgressIcon={uploadProgressIcon}
          emptyStateIcon={smallImageIcon}
          truncateFileName={truncateFileName}
          getOriginalFilename={getOriginalFilename}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onChooseFile={handleChooseFile}
          onFileInputChange={handleFileInputChange}
          onOpenUploadCancelConfirmation={handleOpenUploadCancelConfirmation}
          onOpenDeleteConfirmation={handleOpenDeleteConfirmation}
        />

        <BackupImagePreviewPanel
          previewUrl={displayedPreviewUrl}
          emptyPreviewIcon={uploadFileToPreview}
          previewWidth={currentBannerDimensions.width}
          previewHeight={currentBannerDimensions.height}
        />
      </div>

      <BackupImageSettingsFooter
        saving={saving}
        isSaveDisabled={isSaveDisabled}
        onCancel={handleFooterCancelClick}
        onSave={handleSaveSettings}
      />

      <BackupImageCancelConfirmationDialog
        cancelConfirmationType={cancelConfirmationType}
        selectedFileName={displayedSelectedFile?.name}
        onDialogClose={handleCancelConfirmationDialogClose}
        onClose={handleCloseCancelConfirmation}
        onConfirmUploadCancel={handleConfirmUploadCancel}
        onConfirmDeleteImage={handleConfirmDeleteImage}
        onConfirmBackToEditor={handleConfirmBackToEditor}
        getOriginalFilename={getOriginalFilename}
      />
    </div>
  );
}
